import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperclip, FaHourglassHalf, FaWallet, FaCheckCircle, FaSearch } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import HeaderCard from '../../components/Header/HeaderCard';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { formaterMois, formaterMontant } from '../Espace/formatAvances';
import { ouvrirJustificatif } from '../Espace/justificatif';
import ParcoursNoteFrais from '../Espace/ParcoursNoteFrais';
import { useATraiter } from '../../components/Navigation/ATraiter';

/**
 * Page RH des notes de frais (Yros/Kavero#18, tâche T4 : la vue de validation
 * RH, passée du backoffice Odoo au portail).
 *
 * Constaté le 21/09/2026 sur la supervision : la note de frais y apparaissait,
 * mais les boutons de décision sortaient du cadre à droite, et rien ne disait
 * ce que devenait le remboursement. La RH valide une dépense sur pièce, puis
 * en suit le versement : cette page fait les deux.
 *
 * Les notes à valider s'affichent d'abord, la plus ancienne en tête — une file
 * se traite dans l'ordre d'arrivée. La décision est sur la ligne, avec les
 * reçus, dans une grille qui se replie sur le téléphone au lieu de défiler.
 *
 * Les données sont celles de la supervision, filtrées sur la note de frais :
 * même route, même garde, et les mêmes décisions routées par type.
 */

const FILTRES = ['a_valider', 'validees', 'refusees', 'toutes'];
const ETAT_DU_FILTRE = { a_valider: 'en_attente', validees: 'validee', refusees: 'refusee' };
const PAR_PAGE = 10;
const MOIS_PROPOSES = 12;

const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

// Littérale pour que Tailwind la génère : le salarié, la dépense, le montant,
// le suivi, la décision.
// La décision a une largeur fixe : en `auto`, chaque ligne calculait la
// sienne, et la ligne à valider (liste de mois et boutons) décalait toutes ses
// colonnes par rapport aux lignes déjà décidées.
const GRILLE = 'md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,1.6fr)_13rem]';

/** Minuscules sans accents : « Élodie » se trouve en tapant « elodie ». */
function normaliser(texte) {
  return (texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** « décembre 2026 » → « Décembre 2026 ». `capitalize` en CSS mettrait une
 * majuscule à chaque mot, libellé par défaut compris. */
function majusculeInitiale(texte) {
  return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte;
}

function montantDe(demande) {
  const montant = demande.contenu?.versement?.montant ?? demande.contenu?.montant;
  return typeof montant === 'number' && Number.isFinite(montant) ? montant : 0;
}

function Indicateur({ icone: Icone, libelle, valeur, detail, testId }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4" data-testid={testId}>
      <span className="w-9 h-9 rounded-lg bg-slate-100 text-[--color-primary] flex items-center justify-center flex-shrink-0">
        <Icone className="w-4 h-4" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{libelle}</p>
        <p className="text-lg font-semibold text-slate-900 tabular-nums">{valeur}</p>
        {detail && <p className="text-xs text-slate-500 tabular-nums">{detail}</p>}
      </div>
    </div>
  );
}

function NotesFraisRh() {
  const { t, i18n } = useTranslation();
  const { relire: relireATraiter } = useATraiter();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';

  const [notes, setNotes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [filtre, setFiltre] = useState('a_valider');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);

  // Décision : la note en cours d'envoi, celle dont on saisit le motif, et le
  // refus métier d'Odoo, qui s'affiche sans faire disparaître la liste.
  const [decisionEnCours, setDecisionEnCours] = useState(null);
  const [refusPour, setRefusPour] = useState(null);
  const [motif, setMotif] = useState('');
  const [erreurAction, setErreurAction] = useState(null);
  // Mois de remboursement choisi, par note (Yros/Kavero#437). Vide : Odoo
  // garde son défaut, le mois de validation — ou le suivant s'il est clos.
  const [moisChoisi, setMoisChoisi] = useState({});

  // La dernière requête lancée fait foi (même garde que la supervision).
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniereRequete.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerSupervisionRh();
      if (numero !== derniereRequete.current) return;
      setNotes((reponse?.demandes || []).filter((demande) => demande.type === 'note_frais'));
    } catch (e) {
      if (numero !== derniereRequete.current) return;
      signalerErreur(e, 'NotesFraisRh.charger');
      setNotes([]);
      setErreur(t('notesFraisRh.erreurGenerique'));
    } finally {
      if (numero === derniereRequete.current) setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Une recherche ou un filtre neufs repartent de la première page.
  useEffect(() => {
    setPage(1);
  }, [filtre, recherche]);

  const synthese = useMemo(() => {
    const enAttente = notes.filter((n) => n.etat === 'en_attente');
    const validees = notes.filter((n) => n.etat === 'validee');
    const somme = (liste) => liste.reduce((total, n) => total + montantDe(n), 0);
    return {
      attente: enAttente.length,
      montantAttente: somme(enAttente),
      aVerser: somme(validees.filter((n) => n.contenu?.versement?.statut !== 'verse')),
      verse: somme(validees.filter((n) => n.contenu?.versement?.statut === 'verse')),
    };
  }, [notes]);

  const affichees = useMemo(() => {
    const terme = normaliser(recherche.trim());
    const etat = ETAT_DU_FILTRE[filtre];
    const liste = notes
      .filter((n) => !etat || n.etat === etat)
      .filter((n) => !terme || normaliser(n.demandeur?.nom).includes(terme));
    // À valider : la plus ancienne en tête. Ailleurs : la plus récente.
    const sens = filtre === 'a_valider' ? 1 : -1;
    return [...liste].sort((a, b) => sens * (a.date_demande || '').localeCompare(b.date_demande || ''));
  }, [notes, filtre, recherche]);

  const total = affichees.length;
  const pageMax = Math.max(1, Math.ceil(total / PAR_PAGE));
  const pageCourante = Math.min(page, pageMax);
  const debut = (pageCourante - 1) * PAR_PAGE;
  const visibles = affichees.slice(debut, debut + PAR_PAGE);

  const messageDeRefus = (e) => e?.response?.data?.message || t('notesFraisRh.erreurGenerique');

  // Les mois proposés au remboursement : le mois en cours et les onze
  // suivants. Une liste plutôt qu'un `<input type="month">`, que Safari pour
  // macOS rend en simple champ texte. Jamais un mois passé : Odoo le refuse,
  // et un mois clos se dit dans son message.
  const moisProposes = useMemo(() => {
    const premier = new Date();
    premier.setDate(1);
    return Array.from({ length: MOIS_PROPOSES }, (_, i) => {
      const d = new Date(premier.getFullYear(), premier.getMonth() + i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }, []);

  const approuver = async (demande) => {
    if (decisionEnCours) return;
    setDecisionEnCours(demande.id);
    setErreurAction(null);
    try {
      const mois = moisChoisi[demande.id];
      if (mois) {
        await apiService.approuverDemandeRh(demande.id, demande.type, undefined, mois);
      } else {
        await apiService.approuverDemandeRh(demande.id, demande.type);
      }
      relireATraiter();
      await charger();
    } catch (e) {
      setErreurAction(messageDeRefus(e));
    } finally {
      setDecisionEnCours(null);
    }
  };

  const confirmerLeRefus = async (demande) => {
    // Un motif fait d'espaces n'est pas un motif : rien ne part.
    if (!motif.trim() || decisionEnCours) return;
    setDecisionEnCours(demande.id);
    setErreurAction(null);
    try {
      await apiService.refuserDemandeRh(demande.id, demande.type, motif.trim());
      relireATraiter();
      setRefusPour(null);
      setMotif('');
      await charger();
    } catch (e) {
      setErreurAction(messageDeRefus(e));
    } finally {
      setDecisionEnCours(null);
    }
  };

  const voirJustificatif = async (demande, piece) => {
    setErreurAction(null);
    try {
      await ouvrirJustificatif(demande, piece);
    } catch (e) {
      signalerErreur(e, 'NotesFraisRh.voirJustificatif');
      setErreurAction(t('notesFraisRh.justificatifIndisponible'));
    }
  };

  const compte = (cle) => {
    const etat = ETAT_DU_FILTRE[cle];
    return etat ? notes.filter((n) => n.etat === etat).length : notes.length;
  };

  return (
    <div className="p-4 space-y-4">
      <HeaderCard title={t('notesFraisRh.titre')} text={t('notesFraisRh.description')} />

      {(erreur || erreurAction) && (
        <p
          data-testid="notes-frais-rh-erreur"
          role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
        >
          {erreur || erreurAction}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : !erreur && (
        <>
          <section
            aria-label={t('notesFraisRh.syntheseTitre')}
            className="bg-white rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200"
          >
            <Indicateur
              icone={FaHourglassHalf}
              libelle={t('notesFraisRh.synthese.attente')}
              valeur={synthese.attente}
              detail={formaterMontant(synthese.montantAttente, locale)}
              testId="notes-frais-rh-synthese-attente"
            />
            <Indicateur
              icone={FaWallet}
              libelle={t('notesFraisRh.synthese.aVerser')}
              valeur={formaterMontant(synthese.aVerser, locale)}
              testId="notes-frais-rh-synthese-a-verser"
            />
            <Indicateur
              icone={FaCheckCircle}
              libelle={t('notesFraisRh.synthese.verse')}
              valeur={formaterMontant(synthese.verse, locale)}
              testId="notes-frais-rh-synthese-verse"
            />
          </section>

          <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 border-b border-slate-200">
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('notesFraisRh.filtresTitre')}>
                {FILTRES.map((cle) => (
                  <button
                    key={cle}
                    type="button"
                    data-testid={`notes-frais-rh-filtre-${cle}`}
                    aria-pressed={filtre === cle}
                    onClick={() => setFiltre(cle)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      filtre === cle
                        ? 'bg-[--color-primary] text-white border-[--color-primary]'
                        : 'bg-white text-[--color-primary] border-[--color-primary] hover:bg-slate-50'
                    }`}
                  >
                    {t(`notesFraisRh.filtres.${cle}`)}
                    <span className="ml-1.5 tabular-nums opacity-80">{compte(cle)}</span>
                  </button>
                ))}
              </div>
              <label className="relative w-full sm:w-64">
                <span className="sr-only">{t('notesFraisRh.recherche')}</span>
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" aria-hidden="true" />
                <input
                  type="search"
                  data-testid="notes-frais-rh-recherche"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  placeholder={t('notesFraisRh.recherche')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                />
              </label>
            </div>

            {total === 0 ? (
              <p data-testid="notes-frais-rh-vide" className="px-5 py-10 text-center text-sm text-slate-500">
                {filtre === 'a_valider' && !recherche.trim()
                  ? t('notesFraisRh.vide')
                  : t('notesFraisRh.aucunResultat')}
              </p>
            ) : (
              <div role="table" aria-label={t('notesFraisRh.titre')}>
                <div
                  role="row"
                  className={`hidden md:grid gap-x-4 px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 ${GRILLE}`}
                >
                  <div role="columnheader">{t('notesFraisRh.colonnes.salarie')}</div>
                  <div role="columnheader">{t('notesFraisRh.colonnes.depense')}</div>
                  <div role="columnheader" className="text-right">{t('notesFraisRh.colonnes.montant')}</div>
                  <div role="columnheader">{t('notesFraisRh.colonnes.suivi')}</div>
                  <div role="columnheader"><span className="sr-only">{t('notesFraisRh.colonnes.decision')}</span></div>
                </div>
                <div role="rowgroup" className="divide-y divide-slate-100">
                  {visibles.map((demande) => {
                    const contenu = demande.contenu || {};
                    const enAttente = demande.etat === 'en_attente';
                    return (
                      <div
                        key={demande.id}
                        role="row"
                        data-testid={`notes-frais-rh-ligne-${demande.id}`}
                        className={`grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-3 sm:px-5 md:items-start ${GRILLE}`}
                      >
                        <div role="cell" className="col-span-2 md:col-span-1 min-w-0">
                          <p className="font-medium text-slate-900 break-words">{demande.demandeur?.nom || '—'}</p>
                        </div>
                        <div role="cell" className="col-span-2 md:col-span-1 min-w-0">
                          <p className="text-sm text-slate-900">
                            {t(`notesFrais.natures.${contenu.nature}`, { defaultValue: contenu.nature || '—' })}
                          </p>
                          {contenu.description && (
                            <p className="text-sm text-slate-600 break-words">{contenu.description}</p>
                          )}
                          {(contenu.justificatifs || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                              {contenu.justificatifs.map((piece) => (
                                <button
                                  key={piece.id}
                                  type="button"
                                  data-testid={`notes-frais-rh-justificatif-${demande.id}-${piece.id}`}
                                  onClick={() => voirJustificatif(demande, piece)}
                                  aria-label={`${t('notesFrais.voirJustificatif')} : ${piece.nom}`}
                                  title={piece.nom}
                                  className="inline-flex max-w-full items-center gap-1.5 text-left text-xs font-medium text-[--color-primary] hover:underline"
                                >
                                  <FaPaperclip className="flex-shrink-0" aria-hidden="true" />
                                  <span className="max-w-[14rem] truncate">{piece.nom}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div role="cell" className="min-w-0 md:text-right">
                          <span className="block text-xs text-slate-500 md:hidden">{t('notesFraisRh.colonnes.montant')}</span>
                          <span className="text-sm font-semibold text-slate-900 tabular-nums">{formaterMontant(contenu.montant, locale)}</span>
                        </div>
                        <div role="cell" className="col-span-2 md:col-span-1 min-w-0 space-y-2">
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
                              COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
                            }`}
                          >
                            {t(`notesFrais.etats.${demande.etat}`, { defaultValue: demande.etat })}
                          </span>
                          {/* Les quatre dates de la note : dépense, dépôt,
                              décision, remboursement (Yros/Kavero#437). */}
                          <ParcoursNoteFrais
                            demande={demande}
                            locale={locale}
                            testId={`notes-frais-rh-parcours-${demande.id}`}
                            testIdVersement={`notes-frais-rh-versement-${demande.id}`}
                          />
                          {demande.motif_refus && (
                            <p className="text-sm text-red-800 break-words">{demande.motif_refus}</p>
                          )}
                        </div>
                        <div role="cell" className="col-span-2 md:col-span-1 flex flex-col gap-2">
                          {enAttente && refusPour !== demande.id && (
                            <>
                              <label className="flex flex-col text-xs text-slate-600" htmlFor={`mois-${demande.id}`}>
                                {t('notesFraisRh.moisRemboursement')}
                                <select
                                  id={`mois-${demande.id}`}
                                  data-testid={`notes-frais-rh-mois-${demande.id}`}
                                  value={moisChoisi[demande.id] || ''}
                                  disabled={decisionEnCours !== null}
                                  onChange={(e) => setMoisChoisi((prev) => ({ ...prev, [demande.id]: e.target.value }))}
                                  className="mt-0.5 w-full px-2 py-1.5 text-sm border rounded-lg text-gray-700 bg-white"
                                >
                                  <option value="">{t('notesFraisRh.moisParDefaut')}</option>
                                  {moisProposes.map((mois) => (
                                    <option key={mois} value={mois}>{majusculeInitiale(formaterMois(mois, locale))}</option>
                                  ))}
                                </select>
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                data-testid={`notes-frais-rh-approuver-${demande.id}`}
                                disabled={decisionEnCours !== null}
                                onClick={() => approuver(demande)}
                                className="px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('notesFraisRh.approuver')}
                              </button>
                              <button
                                type="button"
                                data-testid={`notes-frais-rh-refuser-${demande.id}`}
                                disabled={decisionEnCours !== null}
                                onClick={() => {
                                  setRefusPour(demande.id);
                                  setMotif('');
                                  setErreurAction(null);
                                }}
                                className="px-3 py-2 text-sm font-medium rounded-lg border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('notesFraisRh.refuser')}
                              </button>
                              </div>
                            </>
                          )}
                        </div>
                        {refusPour === demande.id && (
                          <div role="cell" className="col-span-2 md:col-span-5 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <label className="block text-sm font-medium text-slate-800" htmlFor={`motif-${demande.id}`}>
                              {t('notesFraisRh.motifTitre')}
                            </label>
                            <textarea
                              id={`motif-${demande.id}`}
                              data-testid={`notes-frais-rh-motif-${demande.id}`}
                              value={motif}
                              onChange={(e) => setMotif(e.target.value)}
                              placeholder={t('notesFraisRh.motifPlaceholder')}
                              rows={2}
                              className="w-full px-3 py-2 border rounded-lg text-sm text-gray-700 bg-white"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                data-testid={`notes-frais-rh-annuler-${demande.id}`}
                                onClick={() => {
                                  setRefusPour(null);
                                  setMotif('');
                                }}
                                className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                              >
                                {t('notesFraisRh.annuler')}
                              </button>
                              <button
                                type="button"
                                data-testid={`notes-frais-rh-confirmer-${demande.id}`}
                                disabled={decisionEnCours !== null}
                                onClick={() => confirmerLeRefus(demande)}
                                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {t('notesFraisRh.confirmer')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3 px-4 py-3 sm:px-5 border-t border-slate-200">
                  <span data-testid="notes-frais-rh-pagination" className="text-sm text-slate-600 tabular-nums">
                    {t('notesFraisRh.plage', { debut: debut + 1, fin: debut + visibles.length, total })}
                  </span>
                  <button
                    type="button"
                    data-testid="notes-frais-rh-page-precedente"
                    disabled={pageCourante <= 1}
                    onClick={() => setPage(pageCourante - 1)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    {t('notesFraisRh.precedente')}
                  </button>
                  <button
                    type="button"
                    data-testid="notes-frais-rh-page-suivante"
                    disabled={pageCourante >= pageMax}
                    onClick={() => setPage(pageCourante + 1)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    {t('notesFraisRh.suivante')}
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default NotesFraisRh;
